import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Mail, ArrowRight, X, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = '/api';

function SubscribeModal({ onClose }) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !email) {
      toast.error('Please fill in First Name, Last Name, and Email.');
      return;
    }
    if (phone && !consentGiven) {
      toast.error('Please check the consent box to continue.');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        first_name: firstName,
        last_name: lastName,
        email,
        phone: phone || '',
        sms_opt_in: !!phone,
        consent_given: consentGiven,
      });
      if (response.data.already_subscribed) {
        toast.info("You're already subscribed!");
      } else if (response.data.reactivated) {
        toast.success('Welcome back! Your subscription has been reactivated.');
      } else {
        toast.success('Successfully subscribed to the Community Impact Calendar Weekly Newsletter!');
      }
      onClose();
    } catch {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="newsletter-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="newsletter-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" data-testid="newsletter-modal-close">
          <X className="h-5 w-5" />
        </button>
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-brand-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="h-7 w-7 text-brand-orange" />
          </div>
          <h3 className="text-xl font-bold text-brand-text-dark">{t('newsletter.modalTitle')}</h3>
          <p className="text-sm text-gray-500 mt-2">{t('newsletter.modalDesc')}</p>
        </div>
        <form onSubmit={handleSubscribe} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="text"
              placeholder={t('newsletter.firstName')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              data-testid="newsletter-modal-first-name"
            />
            <Input
              type="text"
              placeholder={t('newsletter.lastName')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              data-testid="newsletter-modal-last-name"
            />
          </div>
          <Input
            type="email"
            placeholder={t('newsletter.emailAddress')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="newsletter-modal-email"
          />
          <div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-400" />
              <Input
                type="tel"
                placeholder={t('newsletter.phoneOptional')}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1"
                data-testid="newsletter-modal-phone"
              />
            </div>
          </div>
          <div className="border rounded-lg p-3 bg-gray-50/50">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="newsletter-modal-consent-label">
              <input
                type="checkbox"
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-gray-300 text-brand-orange focus:ring-brand-orange accent-orange-500"
                data-testid="newsletter-modal-consent"
              />
              <span className="text-xs text-gray-600 leading-relaxed">
                {t('newsletter.consent')}
                {phone ? <span className="text-red-500 font-medium"> *</span> : ''}
              </span>
            </label>
          </div>
          <Button
            type="submit"
            disabled={isSubmitting || !firstName || !lastName || !email || (phone && !consentGiven)}
            className="w-full bg-brand-orange hover:bg-brand-orange-hover text-white py-5 font-semibold"
            data-testid="newsletter-modal-submit"
          >
            {isSubmitting ? t('newsletter.subscribing') : t('newsletter.subscribeNow')}
          </Button>
        </form>
      </div>
    </div>
  );
}

const NewsletterCTA = ({ variant = 'default', onClose = null }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(variant === 'modal-only');

  const closeModal = () => {
    setShowModal(false);
    if (onClose) onClose();
  };

  if (variant === 'modal-only') {
    return showModal ? <SubscribeModal onClose={closeModal} /> : null;
  }

  if (variant === 'compact') {
    return (
      <>
        {showModal && <SubscribeModal onClose={closeModal} />}
        <div className="bg-gradient-to-r from-brand-blue to-brand-blue-mid rounded-2xl p-6 text-white" data-testid="newsletter-cta-compact">
          <div className="flex items-center gap-3 mb-3">
            <Calendar className="h-6 w-6 text-brand-orange" />
            <h3 className="font-bold text-lg">{t('newsletter.title')} {t('newsletter.titleHighlight')}</h3>
          </div>
          <p className="text-gray-300 text-sm mb-4">{t('newsletter.desc')}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/impact-calendar">
              <Button className="bg-white text-brand-blue hover:bg-gray-100 font-semibold w-full sm:w-auto" data-testid="cta-view-calendar">
                {t('newsletter.viewCalendar')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Button onClick={() => setShowModal(true)} variant="outline" className="border-white text-white hover:bg-white/10 font-semibold" data-testid="cta-subscribe-newsletter">
              <Mail className="mr-2 h-4 w-4" /> {t('newsletter.subscribe')}
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {showModal && <SubscribeModal onClose={closeModal} />}
      <section className="py-16 bg-gradient-to-br from-brand-blue via-brand-blue-mid to-brand-blue" data-testid="newsletter-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-brand-orange/20 rounded-xl flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-brand-orange" />
                </div>
                <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">{t('newsletter.stayConnected')}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('newsletter.title')} <span className="text-brand-orange">{t('newsletter.titleHighlight')}</span>
              </h2>
              <p className="text-gray-300 text-base mb-6">
                {t('newsletter.desc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/impact-calendar">
                  <Button className="bg-brand-orange hover:bg-brand-orange-hover text-white px-6 py-5 text-base font-semibold w-full sm:w-auto" data-testid="cta-section-view-calendar">
                    {t('newsletter.exploreCalendar')} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button onClick={() => setShowModal(true)} variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-brand-blue px-6 py-5 text-base font-semibold" data-testid="cta-section-subscribe">
                  <Mail className="mr-2 h-5 w-5" /> {t('newsletter.subscribe')}
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-white font-bold text-lg mb-4">{t('newsletter.modalTitle')}</h3>
                <ul className="space-y-3 text-gray-200 text-sm">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-brand-orange/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Calendar className="h-3.5 w-3.5 text-brand-orange" />
                    </div>
                    {t('newsletter.benefit1')}
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-brand-orange/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Calendar className="h-3.5 w-3.5 text-brand-orange" />
                    </div>
                    {t('newsletter.benefit2')}
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-brand-orange/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Calendar className="h-3.5 w-3.5 text-brand-orange" />
                    </div>
                    {t('newsletter.benefit3')}
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-brand-orange/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Calendar className="h-3.5 w-3.5 text-brand-orange" />
                    </div>
                    {t('newsletter.benefit4')}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NewsletterCTA;
